import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  TrendingUp, TrendingDown, DollarSign, Plus, Pencil, Trash2, Download, Loader2, FileSpreadsheet,
  ChevronRight, ChevronDown, Receipt, Sparkles, Upload,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_METHODS = ['wire', 'cheque', 'cash', 'e-transfer', 'other'];

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  partial: 'bg-blue-100 text-blue-800 border-blue-200',
  paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const EMPTY_ENTRY = {
  direction: 'receivable',
  counterparty_type: 'college',
  counterparty_name: '',
  counterparty_id: '',
  student_id: '',
  student_name_snapshot: '',
  invoice_number: '',
  invoice_date: new Date().toISOString().slice(0, 10),
  expected_amount: '',
  paid_amount: '',
  payment_date: '',
  payment_method: '',
  reference: '',
  note: '',
};

const cad = (n) => `C$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Admin-only Finance Ledger: two-sided invoice & payment tracker.
 *
 * - Receivables: money coming IN from colleges (per student)
 * - Payables: money going OUT to recruiters (per student)
 *
 * Dashboard shows the 5 KPIs the user asked for; each tab is a filterable
 * table with inline create/edit/delete.
 */
export default function FinanceLedgerPanel() {
  const [summary, setSummary] = useState(null);
  const [direction, setDirection] = useState('receivable');  // 'receivable' | 'payable'
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal state
  const [editing, setEditing] = useState(null);   // null = closed, {} = new, {...} = edit
  const [saving, setSaving] = useState(false);

  // Expanded rows + add-payment modal state
  const [expandedId, setExpandedId] = useState(null);
  const [payingEntry, setPayingEntry] = useState(null);   // which entry we're adding a payment to
  const [paymentDraft, setPaymentDraft] = useState({ amount: '', date: new Date().toISOString().slice(0, 10), method: '', reference: '', note: '' });
  const [savingPayment, setSavingPayment] = useState(false);

  // -------------------------------------------------------------------
  // Data loaders
  // -------------------------------------------------------------------
  const loadSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/finance/summary`);
      setSummary(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load finance summary');
    }
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { direction, limit: 500 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/admin/finance/entries`, { params });
      setEntries(data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [direction, statusFilter, search]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  // -------------------------------------------------------------------
  // Create / Edit
  // -------------------------------------------------------------------
  const openNew = () => setEditing({ ...EMPTY_ENTRY, direction });
  const openEdit = (row) => setEditing({ ...row, expected_amount: String(row.expected_amount ?? ''), paid_amount: String(row.paid_amount ?? '') });

  const saveEntry = async () => {
    if (!editing) return;
    const expected = parseFloat(editing.expected_amount);
    if (!editing.counterparty_name.trim()) { toast.error('Counterparty is required.'); return; }
    if (!(expected > 0)) { toast.error('Expected amount must be greater than zero.'); return; }
    const payload = {
      direction: editing.direction,
      counterparty_type: editing.counterparty_type,
      counterparty_name: editing.counterparty_name.trim(),
      counterparty_id: editing.counterparty_id || null,
      student_id: editing.student_id || null,
      student_name_snapshot: editing.student_name_snapshot || null,
      invoice_number: editing.invoice_number || null,
      invoice_date: editing.invoice_date || null,
      expected_amount: expected,
      note: editing.note || '',
    };
    setSaving(true);
    try {
      if (editing.id) {
        await axios.patch(`${API}/admin/finance/entries/${editing.id}`, payload);
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/admin/finance/entries`, { ...payload, paid_amount: 0 });
        toast.success('Entry created · add payments as money moves');
      }
      setEditing(null);
      await Promise.all([loadSummary(), loadEntries()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (row) => {
    if (!window.confirm(`Delete this ${row.direction} for ${row.counterparty_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/finance/entries/${row.id}`);
      toast.success('Entry deleted');
      await Promise.all([loadSummary(), loadEntries()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // ---------------------------------------------------------------------
  // Payment installments (multi-tranche flow)
  // ---------------------------------------------------------------------
  const openAddPayment = (entry) => {
    const outstanding = (entry.expected_amount || 0) - (entry.paid_amount || 0);
    setPayingEntry(entry);
    setPaymentDraft({
      amount: outstanding > 0 ? String(outstanding) : '',
      date: new Date().toISOString().slice(0, 10),
      method: '',
      reference: '',
      note: '',
    });
  };

  const savePayment = async () => {
    if (!payingEntry) return;
    const amt = parseFloat(paymentDraft.amount);
    if (!(amt > 0)) { toast.error('Payment amount must be greater than zero.'); return; }
    if (!paymentDraft.date) { toast.error('Payment date is required.'); return; }
    setSavingPayment(true);
    try {
      await axios.post(`${API}/admin/finance/entries/${payingEntry.id}/payments`, {
        amount: amt,
        date: paymentDraft.date,
        method: paymentDraft.method || null,
        reference: paymentDraft.reference || null,
        note: paymentDraft.note || null,
      });
      toast.success('Payment recorded');
      setPayingEntry(null);
      await Promise.all([loadSummary(), loadEntries()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record payment');
    } finally {
      setSavingPayment(false);
    }
  };

  const deletePayment = async (entry, payment) => {
    if (!window.confirm(`Remove payment of C$${payment.amount.toLocaleString()} from ${payment.date}?`)) return;
    try {
      await axios.delete(`${API}/admin/finance/entries/${entry.id}/payments/${payment.id}`);
      toast.success('Payment removed');
      await Promise.all([loadSummary(), loadEntries()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove payment');
    }
  };

  // -------------------------------------------------------------------
  // Excel export
  // -------------------------------------------------------------------
  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/finance/export.xlsx`, {
        params: { direction },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `finance_${direction}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  const totalForTab = useMemo(() => entries.reduce((a, r) => a + (r.expected_amount || 0), 0), [entries]);
  const paidForTab = useMemo(() => entries.reduce((a, r) => a + (r.paid_amount || 0), 0), [entries]);
  const outstandingForTab = totalForTab - paidForTab;

  return (
    <div className="space-y-6" data-testid="finance-ledger-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Finance Ledger</h1>
          <p className="text-sm text-gray-500 mt-1">Two-sided tracker: money IN from colleges, money OUT to recruiters. CAD only.</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard
          icon={<TrendingUp size={16} className="text-emerald-600" />}
          label="Total Received"
          value={summary ? cad(summary.total_received) : '…'}
          subtle="from colleges"
          testid="kpi-total-received"
        />
        <KpiCard
          icon={<TrendingDown size={16} className="text-rose-600" />}
          label="Total Paid"
          value={summary ? cad(summary.total_paid) : '…'}
          subtle="to recruiters"
          testid="kpi-total-paid"
        />
        <KpiCard
          icon={<DollarSign size={16} className="text-indigo-600" />}
          label="Net Margin"
          value={summary ? cad(summary.net_margin) : '…'}
          subtle="received − paid"
          testid="kpi-net-margin"
          highlight
        />
        <KpiCard
          icon={<TrendingUp size={16} className="text-amber-600" />}
          label="Outstanding Receivables"
          value={summary ? cad(summary.outstanding_receivables) : '…'}
          subtle="colleges still owe us"
          testid="kpi-outstanding-receivables"
        />
        <KpiCard
          icon={<TrendingDown size={16} className="text-amber-600" />}
          label="Outstanding Payables"
          value={summary ? cad(summary.outstanding_payables) : '…'}
          subtle="we still owe recruiters"
          testid="kpi-outstanding-payables"
        />
      </div>

      {/* Tabs + Toolbar */}
      <div className="flex items-center gap-2 flex-wrap border-b border-gray-200">
        {[
          { id: 'receivable', label: 'Receivables', hint: 'Money IN · College → SA' },
          { id: 'payable',    label: 'Payables',    hint: 'Money OUT · SA → Recruiter' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setDirection(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              direction === t.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
            data-testid={`finance-tab-${t.id}`}
          >
            {t.label}
            <span className="ml-2 text-[11px] font-normal text-gray-400">{t.hint}</span>
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 py-2">
          <Input
            placeholder="Search counterparty / invoice # / student"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-64 text-sm"
            data-testid="finance-search"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="finance-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={handleExport} className="h-8 px-3 text-xs" data-testid="finance-export">
            <FileSpreadsheet size={13} className="mr-1" /> Excel
          </Button>
          <Button size="sm" onClick={openNew} className="h-8 px-3 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="finance-new-entry">
            <Plus size={13} className="mr-1" /> New {direction}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">Counterparty</th>
                <th className="px-4 py-3">Student</th>
                <th className="px-4 py-3">Invoice #</th>
                <th className="px-4 py-3">Invoice Date</th>
                <th className="px-4 py-3 text-right">Expected</th>
                <th className="px-4 py-3 text-right">Paid</th>
                <th className="px-4 py-3 text-right">Outstanding</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="px-4 py-6 text-center text-sm text-gray-500">
                  <Loader2 size={14} className="inline mr-2 animate-spin" /> Loading…
                </td></tr>
              )}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-sm text-gray-400">
                  No {direction} entries yet. Click <strong>New {direction}</strong> to add one.
                </td></tr>
              )}
              {!loading && entries.map(r => {
                const outstanding = (r.expected_amount || 0) - (r.paid_amount || 0);
                const payments = r.payments || [];
                const isOpen = expandedId === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50/50">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setExpandedId(isOpen ? null : r.id)}
                            className="w-5 h-5 rounded hover:bg-gray-200 flex items-center justify-center shrink-0"
                            title={isOpen ? 'Hide payments' : 'Show payments'}
                            data-testid={`finance-expand-${r.id}`}
                          >
                            {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          </button>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900">{r.counterparty_name}</div>
                            <div className="text-[11px] text-gray-500 capitalize">{r.counterparty_type}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">{r.student_name_snapshot || <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{r.invoice_number || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-700">{r.invoice_date || '—'}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">{cad(r.expected_amount)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-700">
                        {cad(r.paid_amount)}
                        {payments.length > 1 && <span className="text-[10px] text-gray-400 ml-1">({payments.length}×)</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-amber-700">{cad(outstanding)}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className={`text-[10px] font-semibold capitalize ${STATUS_BADGE[r.status] || ''}`}>{r.status}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-[11px] text-gray-600">
                        {payments.length > 0 ? (
                          <>Last: {payments[payments.length - 1].date}<br /><span className="text-gray-400">{payments[payments.length - 1].method || ''}</span></>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          {outstanding > 0.009 && (
                            <Button size="sm" variant="ghost" onClick={() => openAddPayment(r)} className="h-7 px-2 text-[11px] text-emerald-700 hover:text-emerald-800" data-testid={`finance-add-payment-${r.id}`}>
                              <Receipt size={12} className="mr-1" /> + Payment
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 p-0" data-testid={`finance-edit-${r.id}`}>
                            <Pencil size={13} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteEntry(r)} className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700" data-testid={`finance-delete-${r.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50/60 border-b border-gray-100" data-testid={`finance-payments-${r.id}`}>
                        <td colSpan={10} className="px-10 py-3">
                          <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-2 flex items-center gap-2">
                            <Receipt size={12} /> Payment history ({payments.length})
                          </div>
                          {payments.length === 0 ? (
                            <div className="text-xs text-gray-500 py-2 italic">
                              No payments recorded yet. Click <strong>+ Payment</strong> to add the first installment.
                            </div>
                          ) : (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400">
                                  <th className="pb-1 w-24">Date</th>
                                  <th className="pb-1 text-right w-32">Amount</th>
                                  <th className="pb-1 w-24">Method</th>
                                  <th className="pb-1">Reference</th>
                                  <th className="pb-1">Note</th>
                                  <th className="pb-1 w-10"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {payments.map(p => (
                                  <tr key={p.id} className="border-t border-gray-200">
                                    <td className="py-1.5 font-mono">{p.date}</td>
                                    <td className="py-1.5 text-right font-mono text-emerald-700">{cad(p.amount)}</td>
                                    <td className="py-1.5 capitalize">{p.method || '—'}</td>
                                    <td className="py-1.5 font-mono text-gray-600">{p.reference || '—'}</td>
                                    <td className="py-1.5 text-gray-500">{p.note || ''}</td>
                                    <td className="py-1.5 text-right">
                                      <button
                                        onClick={() => deletePayment(r, p)}
                                        className="text-rose-600 hover:text-rose-800"
                                        title="Remove this payment"
                                        data-testid={`finance-delete-payment-${p.id}`}
                                      >
                                        <Trash2 size={11} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            {entries.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-300 text-xs">
                  <td colSpan={4} className="px-4 py-2 font-semibold text-gray-700 uppercase tracking-wider">Totals ({entries.length})</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold text-gray-900">{cad(totalForTab)}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold text-emerald-700">{cad(paidForTab)}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold text-amber-700">{cad(outstandingForTab)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Create / Edit modal */}
      {editing !== null && (
        <EntryEditor
          entry={editing}
          setEntry={setEditing}
          onClose={() => setEditing(null)}
          onSave={saveEntry}
          saving={saving}
        />
      )}

      {/* Add Payment mini-modal (multi-tranche flow) */}
      {payingEntry && (
        <Dialog open={true} onOpenChange={(v) => { if (!v) setPayingEntry(null); }}>
          <DialogContent className="max-w-lg" data-testid="finance-add-payment-modal">
            <DialogHeader>
              <DialogTitle>Record payment · {payingEntry.counterparty_name}</DialogTitle>
              <DialogDescription className="text-xs">
                Invoice {payingEntry.invoice_number || '—'} · Expected {cad(payingEntry.expected_amount)} · Already paid {cad(payingEntry.paid_amount)} · Outstanding <span className="font-semibold text-amber-700">{cad((payingEntry.expected_amount || 0) - (payingEntry.paid_amount || 0))}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label className="text-xs">Amount (CAD) *</Label>
                <Input type="number" step="0.01" min="0" value={paymentDraft.amount} onChange={(e) => setPaymentDraft(p => ({ ...p, amount: e.target.value }))} className="h-9 font-mono text-sm" data-testid="finance-payment-amount" />
              </div>
              <div>
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={paymentDraft.date} onChange={(e) => setPaymentDraft(p => ({ ...p, date: e.target.value }))} className="h-9 text-sm" data-testid="finance-payment-date" />
              </div>
              <div>
                <Label className="text-xs">Method</Label>
                <Select value={paymentDraft.method || ''} onValueChange={(v) => setPaymentDraft(p => ({ ...p, method: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Reference</Label>
                <Input value={paymentDraft.reference} onChange={(e) => setPaymentDraft(p => ({ ...p, reference: e.target.value }))} className="h-9 text-sm font-mono" placeholder="Wire ref / cheque #" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Note</Label>
                <Textarea value={paymentDraft.note} onChange={(e) => setPaymentDraft(p => ({ ...p, note: e.target.value }))} rows={2} className="text-sm" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayingEntry(null)} disabled={savingPayment}>Cancel</Button>
              <Button onClick={savePayment} disabled={savingPayment} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="finance-payment-save">
                {savingPayment ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : 'Record payment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, subtle, testid, highlight }) {
  return (
    <div className={`p-4 rounded-xl border ${highlight ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-200'}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-gray-500">
        {icon} {label}
      </div>
      <div className={`mt-1.5 text-xl font-bold ${highlight ? 'text-indigo-900' : 'text-gray-900'}`} style={{ fontFamily: "'Libre Baskerville', serif" }}>
        {value}
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5">{subtle}</div>
    </div>
  );
}

function EntryEditor({ entry, setEntry, onClose, onSave, saving }) {
  const upd = (k, v) => setEntry(prev => ({ ...prev, [k]: v }));
  const isEdit = !!entry.id;
  const [scanning, setScanning] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleAiScan = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';   // reset so re-upload of the same file still fires
    if (!f) return;
    setScanning(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const { data } = await axios.post(`${API}/admin/finance/extract-invoice`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const ex = data?.extracted || {};
      // Merge only the keys the model actually returned — leave anything the
      // user has typed intact.
      setEntry(prev => ({
        ...prev,
        counterparty_name: ex.counterparty_name ?? prev.counterparty_name,
        counterparty_type: ex.counterparty_type ?? prev.counterparty_type,
        invoice_number: ex.invoice_number ?? prev.invoice_number,
        invoice_date: ex.invoice_date ?? prev.invoice_date,
        expected_amount: ex.expected_amount != null ? String(ex.expected_amount) : prev.expected_amount,
        student_name_snapshot: ex.student_name_snapshot ?? prev.student_name_snapshot,
        note: ex.notes ? (prev.note ? `${prev.note}\n${ex.notes}` : ex.notes) : prev.note,
      }));
      const filled = ['counterparty_name', 'counterparty_type', 'invoice_number', 'invoice_date', 'expected_amount', 'student_name_snapshot', 'notes']
        .filter(k => ex[k] != null && ex[k] !== '').length;
      if (filled === 0) {
        toast.warning('AI couldn\'t confidently read any fields — please fill manually.');
      } else {
        toast.success(`AI pre-filled ${filled} field${filled > 1 ? 's' : ''} · please review before saving`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI scan failed — please fill manually');
    } finally {
      setScanning(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="finance-editor-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit' : 'New'} {entry.direction === 'receivable' ? 'Receivable' : 'Payable'}</DialogTitle>
          <DialogDescription className="text-xs">
            {entry.direction === 'receivable' ? 'Money coming IN · typically from a college (ITD Canada).' : 'Money going OUT · typically to a recruiter for their commission.'}
            {' '}Record individual payments through the <strong>+ Payment</strong> button after creating the entry.
          </DialogDescription>
        </DialogHeader>

        {/* AI auto-fill strip (new entries only — editing existing uses the plain form) */}
        {!isEdit && (
          <div className="mt-2 mb-1 flex items-center gap-2 p-3 rounded-lg bg-gradient-to-r from-indigo-50 to-sky-50 border border-indigo-100">
            <Sparkles size={16} className="text-indigo-600 shrink-0" />
            <div className="text-xs text-indigo-900 flex-1">
              <strong>Scan an invoice PDF</strong> — the AI reads counterparty, amount, invoice # and date, and pre-fills the form for you to review.
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              onChange={handleAiScan}
              className="hidden"
              data-testid="finance-ai-scan-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={scanning}
              className="h-8 px-3 text-xs border-indigo-300 bg-white hover:bg-indigo-50"
              data-testid="finance-ai-scan-btn"
            >
              {scanning
                ? <><Loader2 size={13} className="mr-1 animate-spin" /> Scanning…</>
                : <><Upload size={13} className="mr-1" /> Scan PDF</>}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs">Direction</Label>
            <Select value={entry.direction} onValueChange={(v) => upd('direction', v)} disabled={isEdit}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="receivable">Receivable (IN)</SelectItem>
                <SelectItem value="payable">Payable (OUT)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Counterparty Type</Label>
            <Select value={entry.counterparty_type} onValueChange={(v) => upd('counterparty_type', v)}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="college">College</SelectItem>
                <SelectItem value="recruiter">Recruiter</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Counterparty name *</Label>
            <Input value={entry.counterparty_name} onChange={(e) => upd('counterparty_name', e.target.value)} placeholder="e.g. ITD Canada / Acme Recruiters" className="h-9 text-sm" data-testid="finance-field-counterparty" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Student name (optional)</Label>
            <Input value={entry.student_name_snapshot || ''} onChange={(e) => upd('student_name_snapshot', e.target.value)} placeholder="For per-student tracking" className="h-9 text-sm" data-testid="finance-field-student" />
          </div>
          <div>
            <Label className="text-xs">Invoice #</Label>
            <Input value={entry.invoice_number || ''} onChange={(e) => upd('invoice_number', e.target.value)} className="h-9 text-sm" data-testid="finance-field-invoice-number" />
          </div>
          <div>
            <Label className="text-xs">Invoice date</Label>
            <Input type="date" value={entry.invoice_date || ''} onChange={(e) => upd('invoice_date', e.target.value)} className="h-9 text-sm" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Expected amount (CAD) *</Label>
            <Input type="number" step="0.01" min="0" value={entry.expected_amount} onChange={(e) => upd('expected_amount', e.target.value)} className="h-9 text-sm font-mono" data-testid="finance-field-expected" />
            <p className="text-[10px] text-gray-400 mt-1">Payment installments are tracked separately — click <strong>+ Payment</strong> on the row after saving.</p>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Note</Label>
            <Textarea value={entry.note || ''} onChange={(e) => upd('note', e.target.value)} rows={2} className="text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="finance-save-btn">
            {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : (isEdit ? 'Save changes' : 'Create entry')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
